import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, StrictMode, Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { AppProvider } from "@shopify/polaris";
import enTranslations from "@shopify/polaris/locales/en.json";
import PageLoading from "@/components/PageLoading";
import { AlertProvider } from "@/app/providers/Alert/AlertProvider";
import { AuthProvider } from "@/app/providers/Auth/AuthProvider";
import { useAuth } from "@/app/providers/Auth/hooks/useAuth";
import { queryClient } from "@/lib/queryClient";

const AdminLayout = lazy(() => import("@/app/layouts/AdminLayout"));
const AuthLayout = lazy(() => import("@/app/layouts/AuthLayout"));
const ReactQueryDevtools =
  process.env.NODE_ENV === "development"
    ? lazy(async () => ({
        default: (await import("@tanstack/react-query-devtools"))
          .ReactQueryDevtools,
      }))
    : null;

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoading fullWidth />;
  }

  return (
    <Suspense fallback={<PageLoading fullWidth />}>
      {user ? <AdminLayout /> : <AuthLayout />}
    </Suspense>
  );
}

export default function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppProvider i18n={enTranslations} theme="light">
          <BrowserRouter>
            <AuthProvider>
              <AlertProvider>
                <AppContent />
                {ReactQueryDevtools ? (
                  <Suspense fallback={null}>
                    <ReactQueryDevtools initialIsOpen={false} />
                  </Suspense>
                ) : null}
              </AlertProvider>
            </AuthProvider>
          </BrowserRouter>
        </AppProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}
